// Calculs de calendrier fondés sur des instants (millisecondes UTC) et des fuseaux IANA.
// Gère les journées de 23 et 25 h, les décalages fractionnaires (+05:30, +05:45),
// les horaires traversant minuit. Aucune lecture de l’heure réelle ici : `now` est
// toujours un paramètre, ce qui permet des tests à horloge figée.

export const MIN = 60_000;
export const STEP = 15 * MIN;
export const DAY = 24 * 60 * MIN;

export interface ZoneInfo {
  /** Date locale 'AAAA-MM-JJ'. */
  date: string;
  h: number;
  m: number;
  s: number;
  /** Minutes écoulées depuis minuit local. */
  minutes: number;
  /** Jour de la semaine local, 0 = dimanche. */
  dow: number;
  /** Décalage par rapport à UTC, en minutes. */
  offset: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();
export function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(tz, f);
  }
  return f;
}

export function isValidZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || !tz) return false;
  try { formatter(tz); return true; } catch { return false; }
}

function infoRaw(tz: string, ms: number): ZoneInfo {
  const p: Record<string, string> = {};
  for (const x of formatter(tz).formatToParts(ms)) p[x.type] = x.value;
  const year = Number(p.year), month = Number(p.month), day = Number(p.day);
  const h = Number(p.hour) % 24, m = Number(p.minute), s = Number(p.second);
  const date = `${p.year}-${p.month}-${p.day}`;
  const offset = Math.round((Date.UTC(year, month - 1, day, h, m, s) - Math.floor(ms / 1000) * 1000) / MIN);
  return { date, h, m, s, minutes: h * 60 + m, dow: new Date(date + 'T12:00:00Z').getUTCDay(), offset };
}

const infoCache = new Map<string, ZoneInfo>();
/** Heure locale d’un instant dans un fuseau. Mémoïsé sur la clé exacte (l’horloge affiche les secondes). */
export function info(tz: string, ms: number): ZoneInfo {
  const k = tz + '|' + ms;
  let v = infoCache.get(k);
  if (v === undefined) {
    v = infoRaw(tz, ms);
    if (infoCache.size > 20000) infoCache.clear();
    infoCache.set(k, v);
  }
  return v;
}

/** Date 'AAAA-MM-JJ' décalée de n jours (calendrier, sans fuseau). */
export function nextDate(date: string, n = 1): string {
  const d = new Date(date + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

const dayCache = new Map<string, number>();
/** Instant de minuit local pour une date dans un fuseau. Lève une erreur si la date n’existe pas. */
export function dayStart(date: string, tz: string): number {
  const key = date + '|' + tz;
  const hit = dayCache.get(key);
  if (hit !== undefined) return hit;
  const base = Date.parse(date + 'T00:00:00Z');
  if (Number.isNaN(base)) throw new Error('Date invalide.');
  const offsets = new Set<number>();
  for (let i = -48; i <= 48; i += 12) offsets.add(info(tz, base + i * 60 * MIN).offset);
  const matches = [...offsets].map((o) => base - o * MIN).filter((ms) => {
    const p = info(tz, ms);
    return p.date === date && p.minutes === 0;
  });
  let result: number | null = matches.length ? Math.min(...matches) : null;
  if (result === null) {
    // Minuit n’existe pas (changement d’heure à 00:00) : premier instant de la date.
    for (let ms = base - 18 * 60 * MIN; ms <= base + 30 * 60 * MIN; ms += STEP) {
      if (info(tz, ms).date === date) { result = ms; break; }
    }
  }
  if (result === null) throw new Error('Cette date n’existe pas dans le fuseau choisi.');
  dayCache.set(key, result);
  if (dayCache.size > 400) dayCache.delete(dayCache.keys().next().value as string);
  return result;
}

/** Bornes [début, fin) d’une journée civile dans un fuseau ; la journée peut durer 23 ou 25 h. */
export function bounds(date: string, tz: string): { start: number; end: number } {
  const start = dayStart(date, tz);
  let end: number;
  try { end = dayStart(nextDate(date), tz); } catch { end = dayStart(nextDate(date, 2), tz); }
  return { start, end };
}

export const pad = (n: number): string => String(n).padStart(2, '0');

/** Minutes depuis minuit → 'HH:MM' (modulo 24 h). */
export function hm(m: number): string {
  const t = ((m % 1440) + 1440) % 1440;
  return `${pad(Math.floor(t / 60))}:${pad(t % 60)}`;
}

export const time = (tz: string, ms: number): string => hm(info(tz, ms).minutes);

export function offsetText(o: number): string {
  if (o === 0) return 'UTC';
  const a = Math.abs(o);
  return 'UTC' + (o > 0 ? '+' : '−') + Math.floor(a / 60) + (a % 60 ? ':' + pad(a % 60) : '');
}

/** Écart en minutes → '+3 h 30', '−6 h', '+0 h'. */
export function gapText(m: number): string {
  const s = m < 0 ? '−' : '+', a = Math.abs(m), h = Math.floor(a / 60), r = a % 60;
  return s + h + ' h' + (r ? ' ' + pad(r) : '');
}

const prettyCache = new Map<string, Intl.DateTimeFormat>();
/** 'lun. 14 sept.' ou, en version longue, 'lundi 14 septembre 2026'. */
export function prettyDate(ms: number, tz: string, full = false, locale = 'fr-FR'): string {
  const k = locale + '|' + tz + '|' + full;
  let f = prettyCache.get(k);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      weekday: full ? 'long' : 'short',
      day: 'numeric',
      month: full ? 'long' : 'short',
      ...(full ? { year: 'numeric' as const } : {}),
    });
    prettyCache.set(k, f);
  }
  return f.format(ms);
}

/** Horodatage ISO avec décalage explicite, ex. 2026-09-14T09:00:00+02:00. */
export function isoIn(tz: string, ms: number): string {
  const p = info(tz, ms), o = p.offset, sg = o < 0 ? '-' : '+', a = Math.abs(o);
  return `${p.date}T${pad(p.h)}:${pad(p.m)}:00${sg}${pad(Math.floor(a / 60))}:${pad(a % 60)}`;
}

/** '09:00–09:30 UTC+2', avec '(+1 j)' si la fin tombe le lendemain et la transition si l’heure change pendant l’appel. */
export function span(tz: string, slot: { start: number; end: number }): string {
  const a = info(tz, slot.start), b = info(tz, slot.end);
  const next = a.date !== b.date && b.minutes !== 0 ? ' (+1 j)' : '';
  const shift = a.offset !== b.offset ? ' (' + offsetText(a.offset) + ' → ' + offsetText(b.offset) + ')' : ' ' + offsetText(a.offset);
  return time(tz, slot.start) + '–' + time(tz, slot.end) + next + shift;
}

/** Minuscules sans accents, pour les recherches. */
export const normalize = (s: string): string => String(s).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** 'HH:MM' → minutes depuis minuit, NaN si invalide. */
export function parseTime(s: string): number {
  if (!/^\d{2}:\d{2}$/.test(s)) return NaN;
  const [h, m] = s.split(':').map(Number) as [number, number];
  return h < 24 && m < 60 ? h * 60 + m : NaN;
}

export const todayIn = (tz: string, now: number): string => info(tz, now).date;
